/**
 * FI Notifications Page
 *
 * Main page component for FI (Financial Information) notifications management
 * Orchestrates table and drawer components
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DrawerProvider } from 'equal-ds-ui';
import { FINotificationsTable } from './FINotificationsTable';
import { FINotificationsDrawer } from './FINotificationsDrawer';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { fiNotificationsAPI } from '../../../services/fi-notifications-api.service';
import { DEFAULT_PAGE_SIZE, DEFAULT_CURRENT_PAGE } from '../../../constants/pagination.constants';
import type {
  FINotificationsPageProps,
  FINotification,
  FIPAccountGroup,
  FINotificationPermissions,
} from '../../../types/fi-notifications';

/**
 * Extract consent ID from notification accounts
 */
const getConsentId = (notification: FINotification): string => {
  if (notification.consent_data_request?.accounts?.length > 0) {
    return notification.consent_data_request.accounts[0].consent_id;
  }
  return '';
};

/**
 * Group accounts by FIP ID for the drawer display
 */
const groupAccountsByFipId = (notification: FINotification): FIPAccountGroup[] => {
  if (!notification.consent_data_request?.accounts) {
    return [];
  }

  const accounts = notification.consent_data_request.accounts;
  const fipMap = new Map<string, FIPAccountGroup>();

  // Group accounts by fip_id
  accounts.forEach((account) => {
    if (!fipMap.has(account.fip_id)) {
      fipMap.set(account.fip_id, {
        fip_id: account.fip_id,
        accounts: [],
      });
    }

    const group = fipMap.get(account.fip_id)!;
    group.accounts.push({
      linked_reference_number: account.link_reference_number,
      masked_account_number: account.masked_account_number,
      account_type: account.account_type,
      fi_type: account.fi_type,
      fi_status: (notification as any).fi_n_status,
      description: notification.fi_n_description,
    });
  });

  return Array.from(fipMap.values());
};

/**
 * Get user permissions from sessionStorage
 */
const getUserPermissions = (): FINotificationPermissions => {
  try {
    const userInfoEncoded = sessionStorage.getItem('loggedUserInfo');
    if (!userInfoEncoded) {
      return { canDownloadCSV: false, canViewAccountDetails: false };
    }

    const userInfo = JSON.parse(atob(userInfoEncoded));
    const menuList = userInfo.menuList || [];
    const fiNotificationMenu = menuList.find((menu: any) => menu.code === 'FI-Notifications');

    if (!fiNotificationMenu || !fiNotificationMenu.features) {
      return { canDownloadCSV: false, canViewAccountDetails: false };
    }

    const features = fiNotificationMenu.features;
    const canDownloadCSV = features.some((f: any) => f.code?.trim() === 'FINOTIFICATION-DOWNLOADCSV');
    const canViewAccountDetails = features.some((f: any) => f.code?.trim() === 'FINOTIFICATION-ACCOUNTDETAILS');

    return { canDownloadCSV, canViewAccountDetails };
  } catch (error) {
    console.error('Error parsing user permissions:', error);
    return { canDownloadCSV: false, canViewAccountDetails: false };
  }
};

export const FINotificationsPage: React.FC<FINotificationsPageProps> = ({
  routeParams,
  queryParams,
  currentRoute,
}) => {
  console.log('[FINotificationsPage] Mounted with props:', { routeParams, queryParams, currentRoute });

  // Get user permissions
  const permissions = useMemo(() => getUserPermissions(), []);

  // State management
  const [notifications, setNotifications] = useState<FINotification[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(DEFAULT_CURRENT_PAGE);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [isLoading, setIsLoading] = useState(false);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<FINotification | null>(null);
  const [fipAccountGroups, setFipAccountGroups] = useState<FIPAccountGroup[]>([]);

  // Error handling
  const { error, errorMessage, handleError, clearError } = useErrorHandler();

  /**
   * Fetch FI notifications from API
   */
  const fetchNotifications = useCallback(async () => {
    setIsLoading(true);
    clearError();

    try {
      const response = await fiNotificationsAPI.getFINotificationsList({
        limit: pageSize,
        pageno: currentPage,
      });

      const rows = response.data.rows || [];

      // Add derived consent_id to each notification
      const enrichedRows = rows.map((notification) => ({
        ...notification,
        consent_id: getConsentId(notification),
      }));

      setNotifications(enrichedRows);
      setTotalCount(response.data.count || 0);
    } catch (err) {
      handleError(err, { component: 'FINotificationsPage', action: 'fetchNotifications' });
      setNotifications([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, handleError, clearError]);

  // Fetch on mount and when pagination changes
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  /**
   * Handle view account details
   */
  const handleViewDetails = useCallback((notification: FINotification) => {
    console.log('[FINotificationsPage] View details for notification:', notification.notifier_id);
    setSelectedNotification(notification);
    setFipAccountGroups(groupAccountsByFipId(notification));
    setDrawerOpen(true);
  }, []);

  /**
   * Handle close drawer
   */
  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedNotification(null);
    setFipAccountGroups([]);
  }, []);

  /**
   * Handle pagination change
   */
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  /**
   * Handle page size change
   */
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page
  }, []);


  return (
    <DrawerProvider side="right" open={drawerOpen} onOpenChange={setDrawerOpen}>
      <div className="equal-ds-root">
        <div className="h-full flex flex-col">
          {/* Page Header */}
          <div className="mb-4">
            <h1 className="text-2xl font-semibold text-text-primary tracking-wide">
              FI Notifications
            </h1>
            <p className="text-sm text-text-secondary mt-1">
              View and manage Financial Information notifications from various FIPs
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">{errorMessage}</p>
              <button
                onClick={clearError}
                className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Table */}
          <FINotificationsTable
            data={notifications}
            currentPage={currentPage}
            pageSize={pageSize}
            totalItems={totalCount}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onViewDetails={handleViewDetails}
            isLoading={isLoading}
            canShowAccountDetails={permissions.canViewAccountDetails}
            canDownloadCSV={permissions.canDownloadCSV}
          />
        </div>
      </div>

      {/* Drawer - Moved outside content divs to be a sibling */}
      <FINotificationsDrawer
        isOpen={drawerOpen}
        onClose={handleCloseDrawer}
        notification={selectedNotification}
        fipAccountGroups={fipAccountGroups}
      />
    </DrawerProvider>
  );
};
